/**
 * Icon — Material Symbols Rounded (SOA 10 PRO).
 *
 * La fuente se carga desde index.html como subconjunto (`icon_names=`), así
 * que SOLO existen los glifos listados ahí. Si necesitás un ícono nuevo,
 * agregá su nombre a esa URL (en orden alfabético) y recién después usalo
 * acá — un nombre fuera del subconjunto se vería como texto plano.
 *
 * `fill` pinta la variante rellena (FILL 1): se usa en el ítem activo de la
 * navegación. `size` en px (24 en la barra, 20-22 en listas).
 *
 * `lucide` → `Ms` mapping: pantallas que todavía usan lucide siguen
 * funcionando (lucide es la caída para cualquier ícono sin equivalente).
 */
export default function Icon({ name, fill = false, size = 24, className = "", style, ...rest }) {
  return (
    <span aria-hidden="true"
          className={`ms${fill ? " ms-fill" : ""} ${className}`.trim()}
          style={{ fontSize: size, width: size, height: size, ...style }}
          {...rest}>
      {name}
    </span>
  );
}

/**
 * Los textos que vienen del API (p. ej. `estado_meta.icon`) pueden traer un
 * emoji. El Portal no muestra emojis: este mapa los convierte al ícono
 * equivalente del subconjunto de la fuente. Si ya llega un nombre de ícono
 * (`visibility`) se usa tal cual; un emoji sin equivalente da `null` y no se
 * dibuja nada.
 */
const EMOJI_A_ICONO = {
  "👀": "visibility", "🎯": "target", "📅": "calendar_month", "🗓": "calendar_month",
  "✅": "check_circle", "✔": "check_circle", "✓": "check_circle", "⚠": "warning", "🎉": "celebration",
  "💬": "chat_bubble", "✏": "edit", "📝": "edit", "🚀": "campaign", "📣": "campaign", "📢": "campaign",
  "❌": "error", "🔔": "notifications", "📄": "description", "🖼": "image", "🎬": "movie",
};
export function nombreIconoDe(valor) {
  if (!valor) return null;
  const s = String(valor).replace(/\uFE0F/g, "").trim();
  if (/^[a-z_]+$/.test(s)) return s;
  return EMOJI_A_ICONO[s] || EMOJI_A_ICONO[Array.from(s)[0]] || null;
}

/** Ícono (Material Symbols) a partir de un valor que puede ser nombre de ícono o emoji del API. */
export function IconoDe({ valor, size = 16, className = "" }) {
  const name = nombreIconoDe(valor);
  if (!name) return null;
  return <Icon name={name} size={size} className={className}/>;
}

/** Ícono por tipo de pieza (bloque "Te toca decidir"). Siempre devuelve algo. */
export function iconoTipoPieza(tipo = "", nombre = "") {
  const s = `${tipo} ${nombre}`.toLowerCase();
  if (/invitaci/.test(s)) return "mail";
  if (/cartel|ploteo|ploteado|gigantograf|se[ñn]al|lona|banner|pend[oó]n|vinilo/.test(s)) return "signpost";
  if (/charla|campa[ñn]a|comunicaci|interna|anuncio|difusi/.test(s)) return "campaign";
  if (/reel|video|v[ií]deo|short|tiktok|spot/.test(s)) return "movie";
  if (/carrusel|carousel/.test(s)) return "view_carousel";
  if (/historia|story|stories/.test(s)) return "auto_stories";
  if (/present|slide|ppt/.test(s)) return "slideshow";
  if (/gacetilla|nota de prensa|art[ií]culo|blog/.test(s)) return "article";
  if (/impres|flyer|folleto|catálogo|catalogo|tr[ií]ptico|d[ií]ptico/.test(s)) return "print";
  if (/documento|informe|brief|propuesta/.test(s)) return "description";
  if (/dise[ñn]o|gr[aá]fica|arte|kv|logo/.test(s)) return "palette";
  return "image";
}
